import { Modal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import type { CmmInputUniqueValueItem } from "../../../pages/configurations/pages/validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";
import { AsyncSearchSelect } from "./AsyncSearchSelect";
import { useConditionListTopValues } from "./hooks/useConditionListTopValues";

export interface ConditionListTopValuesModalMetaData {
  columnName: string;
  dataType: string;
  topValues: CmmInputUniqueValueItem[];
}

interface ConditionListTopValuesModalProps {
  onClose: () => void;
  onSave: (
    columnName: string,
    newTopValues: CmmInputUniqueValueItem[],
  ) => Promise<void>;
  columnName: string;
  dataType: string;
  topValues: CmmInputUniqueValueItem[];
}

const ConditionListTopValuesModal = ({
  onClose,
  onSave,
  columnName,
  dataType,
  topValues,
}: ConditionListTopValuesModalProps) => {
  const {
    selectedValues,
    saveError,
    options,
    loading,
    searchError,
    badges,
    handleClose,
    handleSave,
    handleChange,
    onSearch,
    removeSelectedValue,
    MIN_SEARCH_LENGTH,
  } = useConditionListTopValues({ onClose, onSave, columnName, topValues });

  return (
    <Modal
      dialogId={ModalId.CONDITION_LIST_TOP_VALUES_MODAL}
      title={`Top Values — ${columnName}`}
      onCancel={handleClose}
      onClose={handleClose}
      onSave={handleSave}
      saveButtonText="Save"
      cancelText="Cancel"
      hideFooter={false}
      showSingleButton={false}
      heightPercentage={60}
    >
      <div className="condition-list-top-values-modal flex min-h-[60vh] flex-col gap-3 overflow-y-auto">
        {saveError && <div className="text-sm text-red-600">{saveError}</div>}
        <div className="flex flex-shrink-0 flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {badges.map((badge, index) => (
              <span
                key={`${badge.text}-${index}`}
                className="inline-flex shrink-0 items-center rounded-md border border-primaryGray-15 bg-primaryGray-16 px-2 py-1 text-[0.6875rem] text-primaryGray-1"
              >
                {badge.text}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-2 flex flex-shrink-0 flex-col border-primaryGray-15">
          <AsyncSearchSelect
            options={options}
            value={selectedValues}
            onChange={handleChange}
            onSearch={onSearch}
            isTypeable
            minSearchLength={MIN_SEARCH_LENGTH}
            placeholder={`Type ${MIN_SEARCH_LENGTH}+ characters to search...`}
            loading={loading}
            emptyMessage="No options found."
            error={searchError ?? undefined}
            renderFooter={() =>
              selectedValues.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2 bg-white p-2">
                  {selectedValues.map((val) => (
                    <span
                      key={val}
                      className="inline-flex items-center gap-1 rounded-md border border-primaryGray-1 bg-primaryGray-16 px-1.5 py-1 text-[0.6875rem] text-primaryGray-1"
                    >
                      {val}
                      <button
                        type="button"
                        onClick={() => removeSelectedValue(val)}
                        className="rounded p-0.5"
                        aria-label={`Remove ${val}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : null
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default ConditionListTopValuesModal;
